import React, { useState } from 'react';
import { Row, Col, Button} from '../../bootstrap/index.jsx'
import Select from '../../common/Select.jsx';
import Errors from '../../common/Errors.jsx';
import EditableList from './EditableList.jsx';
import { ACTION_TYPES } from './phaseReducer.js';

import {
  monitoring_report_update_form_path,
  monitoring_report_delete_form_path,
  monitoring_report_update_form_order_path,
  programme_form_edit_path,
} from '~/routes';

const isConfirmedRow = (row) => !(typeof row.id  == 'string' && row.id.match(/new\_/));

const EditableTextCell = ({
  cell,
  row,
  column: { id },
  updateCell, // This is a custom function that we supplied to our table instance
}) => {

  // We need to keep and update the state of the cell normally
  const [value, setValue] = React.useState(cell.value)

  const onChange = e => {
    setValue(e.target.value)
  }

  // We'll only update the external data when the input is blurred
  const onBlur = () => {
    const nested = id.split('.');
    updateCell(row.index, nested[0], {[nested[1]]: value})
  }

  // If the initialValue is changed external, sync it up with our state
  React.useEffect(() => {
    setValue(cell.value)
  }, [cell.value])

  return row.original.editActive ? 
    <div className='input-group input-group-sm full-width'>
      <input className="form-control" value={value} onChange={onChange} onBlur={onBlur} />
    </div>
  :
    value
}

const EditableCheckBoxCell = ({
  cell,
  row,
  column: { id },
  updateCell, // This is a custom function that we supplied to our table instance
}) => {

  // We need to keep and update the state of the cell normally
  const [value, setValue] = React.useState(cell.value);

  const onChange = e => {
    setValue(e.target.checked);
    updateCell(row.index, id, e.target.checked)
  };

  // If the initialValue is changed external, sync it up with our state
  React.useEffect(() => {
    setValue(cell.value)
  }, [cell.value]);

  const optionalHeaderWidth = Math.floor(document.getElementById('optional-header')?.getBoundingClientRect().width) || 50;

  return row.original.editActive ?
    <div className='input-group input-group-sm full-width'>
      <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', width: optionalHeaderWidth}}>
        <input
          type="checkbox"
          value={value}
          checked={value}
          onChange={onChange} />        
      </div>
    </div>
    :
    I18n.t(`option_${value ? 'yes' : 'no'}`)
};

const EditableSelectCell = ({
  cell,
  row,
  updateCell,
  column: { id },
  programmeId,
  formOptions,
  displayOnlyFormOptions = [],
  onChange,
  updateRow
}) => {

  const handleChange = val => {
    if (typeof onChange == 'function') {
      const value = onChange(val);

      if (typeof value == 'object' && (!cell.value || typeof cell.value != "object")) {
        updateRow(row.index, {...row.original, ...value})
      } else {
        updateCell(row.index, id, value)
      }
    } else {
      updateCell(row.index, id, val)
    }
  }

  const hasGroupedOptions = Array.isArray((formOptions[0] || [])[2]);
  const selectedOption = hasGroupedOptions ? formOptions.map(g => g[2].find(opt => opt[0] == cell.value)).filter(g => g)[0] : formOptions.find(opt => opt[0] == cell.value);

  const options = hasGroupedOptions ?
    (formOptions || []).map(g => ({
      label: g[1],
      options: (g[2] || []).map(opt => ({label: opt[1], value: opt[0]}))
    }))
    :
    (formOptions || []).map(opt => ({label: opt[1], value: opt[0]}))

  //Form from earlier review phase have a "link-<id>" value and carry the actual programme_form_id as the 3rd element
  const programmeFormId = selectedOption?.[2] || cell.value;
  const displayedOption = selectedOption || displayOnlyFormOptions.find(opt => opt[0] == cell.value);

  return row.original.editActive ? 
    <div className='input-group input-group-sm full-width'>
      <Select onChange={handleChange} value={cell.value} options={options} className="form-control nested-multiselect"/>
    </div>
    : displayedOption ? (<>
      <a href={programme_form_edit_path({programme_id: programmeId, form_id: programmeFormId, _options: true})} target="_blank">
        <i className='fa fa-link'/>
      </a> {displayedOption[1]}
    </>) : '-'
}


const ActionCell = ({
  cell,
  row,
  column: { id },
  updateCell,
  onSaveRow,
  onDeleteRow,
  linkedForms,
  updateRow,
  removeRow,
  discardRow
}) => {
  const [saving, setSaving] = React.useState(false);

  const onEditClick = (e) => {
    updateRow(row.index, {...row.original, editActive: true, originalRow: row.original});
  }

  const onCancelClick = (e) => {
    updateCell(row.index, 'editActive', false);
    discardRow(row.index);
  }

  const onSaveClick = (e) => {
    setSaving(true);
    const isNew = typeof row.id == 'string';

    onSaveRow({...row.original, sort_order: row.index}, (link_id) => {
      if (link_id){
        updateRow(row.index, {...row.original, editActive: false, id: link_id, new: isNew});
      }
      setSaving(false);
    })
  }

  const onDeleteClick = (e)  => {
    if (confirm(I18n.t('report_form_remove_confirm'))) {
      setSaving(true);
      onDeleteRow({...row.original, sort_order: row.index}, () => {
        removeRow(row.index);
      })
    }

  }

  return <div className="pull-right"> 
    { 
      row.original.editActive ? 
        <div className='d-flex gap-1'>
          {isConfirmedRow(row) && <Button disabled={saving} size="sm" onClick={onDeleteClick} type="danger"><i className="fa fa-trash"/></Button>}
          <Button disabled={saving} size="sm" onClick={onSaveClick}><i className="fa fa-check"/></Button>
          <Button disabled={saving} size="sm" onClick={onCancelClick} type="default"><i className="fa fa-times"/></Button>
        </div>
      :
        <Button size="sm" onClick={onEditClick}><i className="fa fa-pencil"></i></Button>
    }
   </div>
}


const FormList = ({ phase: { forms: data, type: phaseType, id: phaseId }, formOptions, report, availableLocales, linkedForms = [], dispatch, linkableReviewForms })  => {
  const [errors, setErrors] = useState([]);

  const isFillPhase = phaseType == "fill";
  const isApprovalPhase = phaseType == "approval";

  const columns = React.useMemo(
    () => [
      ...availableLocales.map((locale) => 
        ({
          id: `name.${locale}`,
          Header: `${I18n.t('name')} (${locale})`,
          accessor: (originalRow) => originalRow.name[locale] ? originalRow.name[locale] : '',
          Cell: EditableTextCell
        })
      ),
      {
        id: 'programme_form_id',
        Header: I18n.t('form'),
        accessor: ({ programme_form_id, programme_form_link_id }) => programme_form_link_id ? `link-${programme_form_link_id}` : programme_form_id,
        Cell: (props) => <EditableSelectCell programmeId={report.fund_id} onChange={onUpdateForm} {...props} />
      },
      isFillPhase ? {
        id: 'required',
        Header: <span id='optional-header'>{I18n.t('required')}</span>,
        accessor: (originalRow) => originalRow.required ? originalRow.required : '',
        Cell: (props) => <EditableCheckBoxCell {...props} />
      } : null,
      {
        id: 'actions',
        Header: '',
        accessor: 'id',
        Cell: (props) => <ActionCell {...props} />
      }
    ].filter((c) => c),
    []
  );

  const onSaveRow = (row, callback) => {
    setErrors([]);

    if (!row.programme_form_id && !row.programme_form_link_id){
      callback();
      return window.alert(I18n.t('programme_form_required'))
    } else if (!Object.values(row.name || {}).some(v => v.trim() != "")){
      callback();
      return window.alert(I18n.t('programme_form_name_required'))
    }
    jQuery.post(monitoring_report_update_form_path({id: report.id, _options: true}), {form: row, phase_id: phaseId}, function(data){
      dispatch({
        type: row.new ? ACTION_TYPES.ADD_PHASE_FORM : ACTION_TYPES.UPDATE_PHASE_FORM,
        payload: { newPhase: { programme_form_id: row.programme_form_id, programme_form_link_id: row.programme_form_link_id, name: row.name, id: data.link_id, required: row.required }, phaseId },
      });
      callback(data.link_id);
    })
    .fail((res) => {
      const errors = res?.responseJSON?.errors;
      if (errors) {
        setErrors(errors);
      }
      callback();
    })
  }

  const onDeleteRow = (row, callback) => {
    if (!isConfirmedRow(row)){
      callback();
      return
    }

    // Don't allow deleting links to forms that are used in Review phases
    if (linkedForms.includes(row.id.toString())){
      alert(I18n.t('form_linked_cant_delete'))
      return;
    }

    jQuery.post(monitoring_report_delete_form_path({id: report.id, _options: true}), {form: row, phase_id: phaseId}, function(data){
      dispatch({ type: ACTION_TYPES.REMOVE_PHASE_FORM, payload: { rowId: row.id, phaseId } });
      callback();
    })  
  }

  const onSaveRowOrder = (rows) => {
    jQuery.post(monitoring_report_update_form_order_path({id: report.id, _options: true}),
      {form_order: Object.assign({}, rows.map(r => r.id)), phase_id: phaseId}, function(data){
        dispatch({type: ACTION_TYPES.UPDATE_PHASE_FORM_ORDER, payload: {forms: rows, phaseId}});
      }
    )
  }

  const onUpdateForm = (value) => {
    const isLink = value.toString().match("link");
    return { programme_form_id: isLink ? null : value, programme_form_link_id: isLink ? value.toString().split("-")[1] : null };
  };

  const isReviewPhase = phaseType == "commenting";

  const forms = React.useMemo(
    () =>
      isReviewPhase
        ? linkableReviewForms.length > 0
          ? [
              ["form", I18n.t("new_form"), formOptions.available_review_forms],
              ["link", I18n.t("existing_form"), linkableReviewForms.map((f) => [`link-${f.value}`, f.label, f.programmeFormId])],
            ]
          : formOptions.available_review_forms
        : isFillPhase
          ? formOptions.available_fill_forms
          : formOptions.available_approval_forms,
    [isReviewPhase, isFillPhase, linkableReviewForms, formOptions]
  );

  // Review forms are no longer selectable in fill phase, but there are existing links to them that should still be displayed.
  const displayOnlyFormOptions = isFillPhase ? formOptions.available_approval_forms : [];

  const defaultRow = React.useMemo(
    () => ({
      name: availableLocales.reduce((acc, l) => {
        acc[l] = "";
        return acc;
      }, {}),
      programme_form_id: forms.length > 0 ? Array.isArray(forms[0][2]) ? forms[0][2][0][0] : forms[0][0] : null,
      required: true
    }),
    [forms, availableLocales]
  );

  return <Row>
    <Errors errors={errors} />
    <Col>
      <h3>{I18n.t('forms')}</h3>
      <span className='description'>{I18n.t('form_required_description')}</span>
    </Col>
    <Col>
    <EditableList 
      data={data?.reduce((acc, current) => acc.find(r => r.id == current.id) ? acc : [...acc, current], []) || []}
      columns={columns}
      defaultRow={defaultRow}
      onSaveRowOrder={onSaveRowOrder}
      addRowLabel={I18n.t('add_form')}
      cellMethods={{formOptions: forms, displayOnlyFormOptions: displayOnlyFormOptions, linkedForms: linkedForms, onSaveRow: onSaveRow, onDeleteRow: onDeleteRow}}
      report_type={report.report_type}
      addButtonHiddenOneRow={isApprovalPhase ? true : false}
      draggable={isApprovalPhase ? false : true}
    />
    </Col>
  </Row>

}



export default FormList;